import { useMemo, useState } from "react";

import styled, { createGlobalStyle } from "styled-components";

import type { ColumnsType } from "@similarweb/pro-logic-components";
import { CSS_TABLE_CELL_HOVER_CLASS_NAME, Table } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { IconButton, Masonry } from "@similarweb/ui-components";

import { chosenSites } from "app/common/services/chosenSites";
import { CompareTabsContainer } from "app/components/compareTabs/CompareTabsContainer";
import { GenericEmptyState } from "app/components/React/empty-state/GenericEmptyState";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import { AdModal } from "app/pages/digital-marketing/ad-intelligence/common/ads/modal/AdModal";
import { useDisplayAdvertiserPublisherDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDisplayAdvertiserPublisherDataAndParams";
import { useDurationSubtitleFilter } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDurationSubtitleFilter";
import { useWebSourceSubtitleFilter } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useWebSourceSubtitleFilter";
import type { AdIntelligenceController } from "app/pages/digital-marketing/ad-intelligence/common/shared/types/types";
import { allTrackers } from "app/services/track/track";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { ROUTES as ADVERTISER_ROUTES } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingConstants";
import { displayAdvertisingNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingNavigator";
import { publisherAnalysisNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/publisherAnalysis/publisherAnalysisNavigator";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { AdIntelligenceAdCard } from "../ads/AdIntelligenceAdCard";
import { getLoadingCards } from "../ads/AdIntelligenceAdCardLoading";
import { getColumns, ShowModalButtonWrapper } from "../ads/AdIntelligenceAdsColumnConfig";
import { AdIntelligenceAdsFilters } from "../ads/filters/AdIntelligenceAdsFilters";
import { useAdIntelligenceAdsViewSwitcher, ViewType } from "../ads/hooks/useAdIntelligenceAdsViewSwitcher";
import { useAdIntelligenceDownloadAds } from "../ads/hooks/useAdIntelligenceDownloadAds";
import { useAdsGallery } from "../ads/hooks/useAdsGallery";
import { FiltersContainer, Footer, MasonryContainer, EmptyStateWrapper } from "../ads/StyledComponents";
import { AdIntelligenceHookButton } from "../shared/TableComponents";
import {
    getErrorTypeFromTableNoDataType,
    getNoDataObject,
    hasAdIntelligenceOverviewAccess,
    isAdIntelligenceNoTouchPreviewVariant,
    isDailyGranularityLocked,
    openAdIntelligenceHookModal,
} from "../utils/utils";

import { WidgetContainerWithMarginBottom } from "./StyledComponents";

export const TOP_ADS_TABLE_ID = "top-ads-gallery";
const NUMBER_OF_ADS_PER_PAGE = 10;
const MASONRY_GUTTER = 12;

const columnsCountBreakPoints = {
    0: 2,
    904: 3,
};

const AdCardImageGlobalStyles = createGlobalStyle`
    ${CSS_TABLE_CELL_HOVER_CLASS_NAME} ${ShowModalButtonWrapper} {
        opacity: 1;
    }
`;

const TableContainer = styled.div<{ rowCount: number }>`
    min-height: ${({ rowCount }) => Math.min(10, rowCount) * 154}px;
    border-top: 1px solid ${colorsPalettes.carbon[50]};
`;

interface ITopAdsGalleryProps {
    controller?: AdIntelligenceController;
    isPreview?: boolean;
    isOverviewContext?: boolean;
}

export const RecentAdsGallery = ({
    controller = "advertiser",
    isPreview = false,
    isOverviewContext = false,
}: ITopAdsGalleryProps) => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const [currentSelectedAdIndex, setCurrentSelectedAdIndex] = useState<number | null>(null);
    const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>(params.selectedCampaigns ?? []);
    const [selectedEntity, setSelectedEntity] = useState<string[]>(
        controller === "advertiser" ? (params.selectedPublishers ?? []) : (params.selectedAdvertisers ?? []),
    );
    const [selectedFormat, setSelectedFormat] = useState<string>(params.format ?? "");

    const {
        data = [],
        startDate,
        isLoading,
        totalCount,
        isError,
    } = useAdsGallery(
        controller,
        1,
        10,
        undefined,
        controller === "advertiser" ? selectedCampaigns : undefined,
        selectedFormat,
        selectedEntity,
    );
    const subtitleFilters = useSubtitleFilters("country");
    const durationSubtitleFilter = useDurationSubtitleFilter(startDate);
    const webSourceSubtitleFilter = useWebSourceSubtitleFilter(devicesTypes.TOTAL);
    const combinedSubtitleFilters = [durationSubtitleFilter, ...subtitleFilters, webSourceSubtitleFilter];
    const { config: viewTypeSwitcherConfig, currentView } = useAdIntelligenceAdsViewSwitcher(isLoading);
    const { downloadButtonProps, downloadImagesAsZip } = useAdIntelligenceDownloadAds({
        data,
        itemsPerPage: NUMBER_OF_ADS_PER_PAGE,
        totalCount,
        onNextPage: () => displayAdvertisingNavigation.ads.go(params),
    });

    const hasAdIntelBeta = hasAdIntelligenceOverviewAccess(isPreview);
    const isNoTouchVariant = isAdIntelligenceNoTouchPreviewVariant(isPreview);
    const isCompare = chosenSites.isCompare();

    const handleCardClick = (index: number) => {
        setCurrentSelectedAdIndex(index);
        allTrackers.dispatchTrackEvent("Pop Up", "open", "Ads/More info");
    };

    const onSelectSite = (selectedSite: string) => {
        allTrackers.dispatchTrackEvent("Tab", "click", `Filter Domain/${selectedSite}`);
        setSelectedCampaigns([]);
        setSelectedEntity([]);
        proNavigator.applyUpdateParams({
            adsSelectedSite: selectedSite,
        });
    };

    const handleSeeAll = () => {
        if (hasAdIntelBeta) {
            allTrackers.dispatchTrackEvent("Internal Link", "click", "Advertiser_overview/Top_ads/see_more");
            if (controller === "advertiser") {
                void displayAdvertisingNavigation.ads.go({
                    ...params,
                    selectedCampaigns,
                    selectedPublishers: selectedEntity,
                });
            } else {
                void publisherAnalysisNavigation.ads.go({
                    ...params,
                    webSource: devicesTypes.TOTAL,
                    selectedAdvertisers: selectedEntity,
                    ...(selectedFormat && { format: selectedFormat }),
                });
            }
        } else {
            openAdIntelligenceHookModal(controller);
        }
    };

    const ads = isLoading
        ? getLoadingCards()
        : data.map((item, index) => (
              <AdIntelligenceAdCard
                  key={item.creativeId}
                  onClick={() => handleCardClick(index)}
                  hideDuration={isPreview || isDailyGranularityLocked()}
                  {...item}
              />
          ));

    const columns = useMemo(
        () =>
            getColumns(1, NUMBER_OF_ADS_PER_PAGE, handleCardClick, controller, true) as ColumnsType<
                (typeof data)[number]
            >,
        [],
    );

    const noDataObject = getNoDataObject(isError);

    return (
        <div id={TOP_ADS_TABLE_ID}>
            <WidgetContainerWithMarginBottom
                className="col-xs"
                withBorder={!isCompare}
                showSubtitleFiltersLoadingState
                title={translate("display.advertising.overview.ad.widget.title")}
                tooltip={translate("display.advertising.overview.ad.widget.title.tooltip")}
                subtitleFilters={combinedSubtitleFilters}
                dynamicSwitcherConfig={[viewTypeSwitcherConfig]}
                downloadProps={downloadButtonProps}
                onDownloadClick={void downloadImagesAsZip}
                areSubtitleFiltersLoading={isLoading}
                isLoading={false}
                loaderWidth={"100%"}
                loaderHeight={106}
            >
                {isCompare ? (
                    <CompareTabsContainer
                        onSelectSite={onSelectSite}
                        keyName="adsOverviewFirstLoads"
                        domainQueryParamName={"adsSelectedSite"}
                        hideResults={true}
                    />
                ) : null}
                <FiltersContainer $marginBottom={currentView === ViewType.Table ? 16 : 4}>
                    <AdIntelligenceAdsFilters
                        isCompactView
                        adsPageGoToState={controller === "advertiser" ? ADVERTISER_ROUTES.ads.stateName : undefined}
                        controller={controller}
                        {...(controller === "advertiser" && {
                            selectedCampaigns,
                            onCampaignsFilterChange: setSelectedCampaigns,
                        })}
                        selectedEntity={selectedEntity}
                        onEntityFilterChange={setSelectedEntity}
                        selectedFormat={selectedFormat}
                        onFormatFilterChange={setSelectedFormat}
                        isPreview={isPreview}
                    />
                </FiltersContainer>

                {!isLoading && !ads.length ? (
                    <EmptyStateWrapper>
                        <GenericEmptyState
                            isFullPage={false}
                            title={noDataObject.title}
                            subtitle={noDataObject.subtitle}
                            type={getErrorTypeFromTableNoDataType(noDataObject.type)}
                        />
                    </EmptyStateWrapper>
                ) : currentView === ViewType.Gallery ? (
                    <MasonryContainer>
                        <Masonry
                            breakpoints={columnsCountBreakPoints}
                            gutter={MASONRY_GUTTER}
                        >
                            {...ads}
                        </Masonry>
                    </MasonryContainer>
                ) : (
                    <TableContainer rowCount={data.length}>
                        <Table
                            data={data}
                            isLoading={isLoading}
                            loaderRowCount={NUMBER_OF_ADS_PER_PAGE}
                            columnConfig={columns}
                            size="large"
                            theme="white"
                            pagination={{ pageSize: NUMBER_OF_ADS_PER_PAGE }}
                        />
                    </TableContainer>
                )}
                {totalCount && data.length > 0 ? (
                    <Footer $showBorderTop>
                        {hasAdIntelBeta ? (
                            <IconButton
                                type="flat"
                                onClick={handleSeeAll}
                                iconName="arrow-right"
                                placement="right"
                                iconSize="xs"
                            >
                                {translate("ad.intelligence.overview.top.ads.see.all.button")}
                            </IconButton>
                        ) : (
                            <AdIntelligenceHookButton
                                dataAutomation="ad-intelligence-recent-ads-hook-button"
                                buttonText={translate("ad.intelligence.advertiser.overview.recent.ads.preview.button")}
                                isNoTouchVariant={isNoTouchVariant}
                                onButtonClick={handleSeeAll}
                            />
                        )}
                    </Footer>
                ) : null}
            </WidgetContainerWithMarginBottom>
            <AdModal
                isOpen={currentSelectedAdIndex !== null}
                data={data}
                currentSelectedAdIndex={currentSelectedAdIndex}
                onClose={() => setCurrentSelectedAdIndex(null)}
                onSelectedAdIndexChange={setCurrentSelectedAdIndex}
                controller={controller}
                isOverviewContext={isOverviewContext}
            />
            <AdCardImageGlobalStyles />
        </div>
    );
};
